// Sortable Submitters Element
// Enables drag and drop reordering for party submitters

export default class extends HTMLElement {
  connectedCallback() {
    this.container = this.querySelector('[data-sortable-container]') || this
    this.initDragAndDrop()
  }
  
  initDragAndDrop() {
    // Make all submitter items draggable
    const items = this.querySelectorAll('submitter-item')
    items.forEach((item, index) => {
      item.setAttribute('draggable', 'true')
      item.dataset.index = index
      
      // Store initial contact data from the element
      this.storeContactDataOnElement(item)
      
      item.addEventListener('dragstart', (e) => this.handleDragStart(e, item))
      item.addEventListener('dragend', (e) => this.handleDragEnd(e, item))
      item.addEventListener('dragover', (e) => this.handleDragOver(e, item))
      item.addEventListener('drop', (e) => this.handleDrop(e, item))
    })
  }
  
  // Store contact data directly on the element as data attributes
  storeContactDataOnElement(item) {
    const autocomplete = item.querySelector('new-submitter-autocomplete-v2')
    if (autocomplete) {
      const nameHidden = autocomplete.querySelector('input[name*="[name]"]')
      const emailHidden = autocomplete.querySelector('input[name*="[email]"]')
      
      if (nameHidden && nameHidden.value) {
        item.dataset.contactName = nameHidden.value
      }
      if (emailHidden && emailHidden.value) {
        item.dataset.contactEmail = emailHidden.value
      }
    }
  }
  
  // Update stored contact data from current input values
  updateStoredContactData() {
    const items = this.querySelectorAll('submitter-item')
    items.forEach(item => {
      const autocomplete = item.querySelector('new-submitter-autocomplete-v2')
      if (autocomplete) {
        const nameHidden = autocomplete.querySelector('input[name*="[name]"]')
        const emailHidden = autocomplete.querySelector('input[name*="[email]"]')
        
        if (nameHidden && nameHidden.value) {
          item.dataset.contactName = nameHidden.value
        }
        if (emailHidden && emailHidden.value) {
          item.dataset.contactEmail = emailHidden.value
        }
      }
    })
  }
  
  // Restore contact data from data attributes to the Vue component
  restoreContactDataFromElement(item) {
    const name = item.dataset.contactName || ''
    const email = item.dataset.contactEmail || ''
    
    if (!name && !email) return
    
    const autocomplete = item.querySelector('new-submitter-autocomplete-v2')
    if (!autocomplete) return
    
    // Update hidden inputs
    const nameHidden = autocomplete.querySelector('input[name*="[name]"]')
    const emailHidden = autocomplete.querySelector('input[name*="[email]"]')
    
    if (nameHidden) {
      nameHidden.value = name
      // Dispatch change event for Vue
      nameHidden.dispatchEvent(new Event('change', { bubbles: true }))
    }
    if (emailHidden) {
      emailHidden.value = email
      emailHidden.dispatchEvent(new Event('change', { bubbles: true }))
    }
    
    // Update visible display elements
    const nameDisplay = autocomplete.querySelector('.font-semibold.text-gray-800')
    const emailDisplay = autocomplete.querySelector('.text-gray-500.truncate')
    
    if (nameDisplay) nameDisplay.textContent = name || 'No Name'
    if (emailDisplay) emailDisplay.textContent = email || 'No Email'
    
    // Also update the card's left border to show it has content
    const card = autocomplete.querySelector('.flex.items-center.bg-white')
    if (card && (name || email)) {
      card.classList.add('border-l-4', 'border-l-cs-main-500')
    }
  }
  
  handleDragStart(e, item) {
    this.draggedItem = item
    
    // Update stored data from current input values before dragging
    this.updateStoredContactData()
    
    item.classList.add('opacity-50', 'border-2', 'border-dashed', 'border-cs-main-500', 'rounded-lg')
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', item.dataset.index)
  }
  
  handleDragEnd(e, item) {
    item.classList.remove('opacity-50', 'border-2', 'border-dashed', 'border-cs-main-500', 'rounded-lg')
    
    // Remove all drag-over styling
    this.querySelectorAll('submitter-item').forEach(i => {
      i.classList.remove('border-t-4', 'border-b-4', 'border-cs-main-500', 'pt-4', 'pb-4')
    })
    
    this.draggedItem = null
    
    // Restore contact data for all items after a short delay
    // This gives Vue time to potentially remount components
    setTimeout(() => {
      const items = this.querySelectorAll('submitter-item')
      items.forEach(item => {
        this.restoreContactDataFromElement(item)
      })
      
      // Renumber all items after reordering
      this.renumberItems()
    }, 50)
  }
  
  handleDragOver(e, targetItem) {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    
    if (targetItem === this.draggedItem) return
    
    // Remove previous indicators from all items
    this.querySelectorAll('submitter-item').forEach(i => {
      i.classList.remove('border-t-4', 'border-b-4', 'border-cs-main-500')
    })
    
    // Determine if mouse is in top or bottom half
    const rect = targetItem.getBoundingClientRect()
    const midY = rect.top + rect.height / 2
    
    if (e.clientY < midY) {
      targetItem.classList.add('border-t-4', 'border-cs-main-500')
    } else {
      targetItem.classList.add('border-b-4', 'border-cs-main-500')
    }
  }
  
  handleDrop(e, targetItem) {
    e.preventDefault()
    
    if (!this.draggedItem || targetItem === this.draggedItem) return
    
    // Determine drop position
    const rect = targetItem.getBoundingClientRect()
    const midY = rect.top + rect.height / 2
    
    if (e.clientY < midY) {
      targetItem.parentNode.insertBefore(this.draggedItem, targetItem)
    } else {
      targetItem.parentNode.insertBefore(this.draggedItem, targetItem.nextSibling)
    }
    
    // Remove indicators
    this.querySelectorAll('submitter-item').forEach(i => {
      i.classList.remove('border-t-4', 'border-b-4', 'border-cs-main-500')
    })
  }
  
  renumberItems() {
    const items = this.querySelectorAll('submitter-item')
    const ordinals = ['First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth', 'Seventh', 'Eighth', 'Ninth', 'Tenth']
    
    items.forEach((item, index) => {
      item.dataset.index = index
      
      // Update the title in the autocomplete component
      const autocomplete = item.querySelector('new-submitter-autocomplete-v2')
      if (autocomplete) {
        const ordinal = ordinals[index] || `${index + 1}th`
        const newTitle = `${ordinal} Party`
        
        // Update the title attribute (for reference)
        autocomplete.setAttribute('title', newTitle)
        
        // Find and update the visible title element in the Vue component
        const titleElem = autocomplete.querySelector('.text-gray-700.mb-2')
        if (titleElem) {
          titleElem.textContent = newTitle
        }
        
        // Also update the placeholder text that shows when no contact is selected
        const placeholderElem = autocomplete.querySelector('.text-gray-400.italic')
        if (placeholderElem) {
          placeholderElem.textContent = newTitle
        }
      }
    })
  }
}


